import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const imgFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Apple-like palette
const c = {
  bg: '#F2F2F7',
  card: '#FFFFFF',
  text: '#1D1D1F',
  textSec: '#8E8E93',
  textTer: '#AEAEB2',
  border: '#E5E5EA',
  borderSec: '#C7C7CC',
  accent: '#007AFF',
  accentHover: '#0071E3',
  danger: '#FF3B30',
  dangerHover: '#FF453A',
  success: '#34C759',
  warning: '#FF9F0A',
  draft: '#8E8E93',
};

// ===== LAYOUT =====

export const AdminPage = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: ${c.bg};
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif;
  color: ${c.text};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

export const AdminHeader = styled.header`
  background: rgba(242,242,247,0.92);
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  border-bottom: 0.5px solid ${c.borderSec};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 0 20px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 600px) {
    padding: 0 16px;
    height: auto;
    min-height: 44px;
    flex-wrap: wrap;
    gap: 6px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
`;

export const AdminTitle = styled.h1`
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0;
`;

export const AdminBody = styled.main`
  max-width: ${({ $wide }) => ($wide ? '1400px' : '720px')};
  margin: 0 auto;
  padding: 72px 16px 64px;
  animation: ${fadeIn} 0.25s ease;

  @media (max-width: 600px) {
    padding: 68px 0 48px;
  }
`;

// ===== LIST CARD =====

export const AdminCard = styled.div`
  background: ${c.card};
  border-radius: 10px;
  overflow: hidden;
  margin: 0 0 20px;

  @media (max-width: 600px) {
    border-radius: 0;
  }
`;

export const AdminCardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.1s;
  position: relative;

  &:active {
    background: ${c.bg};
  }

  @media (hover: hover) {
    &:hover {
      background: rgba(0,0,0,0.02);
    }
  }

  & + & {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 78px;
      right: 0;
      height: 0.5px;
      background: ${c.border};
    }
  }

  /* Chevron */
  &::after {
    content: '›';
    color: ${c.textTer};
    font-size: 20px;
    font-weight: 300;
    flex-shrink: 0;
    margin-left: auto;
  }

  @media (max-width: 600px) {
    padding: 10px 16px;
    gap: 12px;
  }
`;

export const Thumbnail = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${c.bg};

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 600px) {
    width: 44px;
    height: 44px;
    border-radius: 6px;
  }
`;

export const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;

  h3 {
    font-size: 16px;
    font-weight: 400;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  p {
    font-size: 13px;
    color: ${c.textSec};
    margin: 1px 0 0;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 4px;
  text-transform: capitalize;
  letter-spacing: 0.01em;
  flex-shrink: 0;

  ${({ $status }) => {
    switch ($status) {
      case 'publicado': return `background: rgba(52,199,89,0.12); color: #248A3D;`;
      case 'rascunho': return `background: rgba(142,142,147,0.12); color: ${c.draft};`;
      case 'vendido': return `background: rgba(255,159,10,0.12); color: #B25000;`;
      case 'inativo': return `background: rgba(255,59,48,0.12); color: #C62828;`;
      default: return `background: rgba(142,142,147,0.12); color: ${c.draft};`;
    }
  }}
`;

export const StarBadge = styled.span`
  font-size: 14px;
  color: ${c.warning};
  flex-shrink: 0;
  line-height: 1;
`;

// ===== GRID CARD VIEW =====

export const AdminGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
  padding: 0 16px;
  margin: 0 0 20px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
    padding: 0 12px;
  }
`;

export const AdminGridCard = styled.div`
  background: ${c.card};
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);

  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0,0,0,0.08);
    }
  }
`;

export const GridThumb = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  background: ${c.bg};
  position: relative;
  overflow: hidden;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const GridBody = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  h3 {
    font-size: 14px;
    font-weight: 500;
    margin: 0;
    line-height: 1.3;
    color: ${c.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  p {
    font-size: 12px;
    color: ${c.textSec};
    margin: 0;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const GridBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  flex-wrap: wrap;
`;

// ===== SEARCH + DISPLAY MODE =====

export const ListToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px 12px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
    padding: 0 12px 10px;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 0 12px;
  border-radius: 9px;
  border: 0.5px solid ${c.borderSec};
  background: ${c.card};
  font-size: 14px;
  color: ${c.text};
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder { color: ${c.textTer}; }
  &:focus {
    border-color: ${c.accent};
    box-shadow: 0 0 0 3px rgba(0,122,255,0.12);
  }
`;

export const DisplayToggle = styled.div`
  display: inline-flex;
  background: ${c.card};
  border: 0.5px solid ${c.border};
  border-radius: 9px;
  padding: 2px;
  flex-shrink: 0;
`;

export const DisplayToggleBtn = styled.button`
  border: 0;
  background: ${({ $active }) => ($active ? c.accent : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : c.text)};
  font-size: 13px;
  padding: 4px 12px;
  border-radius: 7px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 500;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover { opacity: 0.95; }
`;

export const FilterBar = styled.div`
  display: grid;
  grid-template-columns: 2fr repeat(4, 1fr) 1fr 1fr auto;
  gap: 8px;
  padding: 0 16px 12px;
  align-items: end;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;
    padding: 0 12px 10px;
  }
`;

export const FilterField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;

  span {
    font-size: 11px;
    color: ${c.textSec};
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding-left: 2px;
  }

  input, select {
    height: 34px;
    padding: 0 10px;
    border-radius: 8px;
    border: 0.5px solid ${c.borderSec};
    background: ${c.card};
    font-size: 13px;
    color: ${c.text};
    outline: none;
    font-family: inherit;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    min-width: 0;
    width: 100%;

    &::placeholder { color: ${c.textTer}; }
    &:focus {
      border-color: ${c.accent};
      box-shadow: 0 0 0 3px rgba(0,122,255,0.12);
    }
  }
`;

export const ClearFiltersBtn = styled.button`
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 0.5px solid ${c.borderSec};
  background: transparent;
  color: ${c.text};
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover { background: ${c.bg}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

// ===== SECTION HEADER (above grouped cards) =====

export const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${c.textSec};
  text-transform: uppercase;
  padding: 0 16px 6px;
  letter-spacing: 0.02em;

  @media (max-width: 600px) {
    padding: 0 16px 6px;
  }
`;

// ===== BUTTONS =====

export const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 7px 14px;
  font-size: 15px;
  font-weight: 400;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.12s;
  font-family: inherit;

  ${({ $variant }) => {
    switch ($variant) {
      case 'primary': return `
        background: ${c.accent};
        color: #FFFFFF;
        font-weight: 500;
        &:hover { background: ${c.accentHover}; }
      `;
      case 'danger': return `
        background: transparent;
        color: ${c.danger};
        &:hover { background: rgba(255,59,48,0.08); }
      `;
      case 'ghost': return `
        background: transparent;
        color: ${c.accent};
        padding: 7px 10px;
        &:hover { background: rgba(0,122,255,0.08); }
      `;
      case 'success': return `
        background: rgba(52,199,89,0.12);
        color: #1B873B;
        &:hover { background: rgba(52,199,89,0.20); }
      `;
      case 'warning': return `
        background: rgba(255,159,10,0.14);
        color: #B8650D;
        &:hover { background: rgba(255,159,10,0.22); }
      `;
      case 'star': return `
        background: rgba(255,184,0,0.16);
        color: #8A5A00;
        &:hover { background: rgba(255,184,0,0.26); }
      `;
      default: return `
        background: rgba(0,122,255,0.08);
        color: ${c.accent};
        &:hover { background: rgba(0,122,255,0.14); }
      `;
    }
  }}

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export const BtnRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

// ===== LOGIN =====

export const LoginWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 24px;
`;

export const LoginForm = styled.form`
  background: ${c.card};
  border-radius: 14px;
  padding: 40px 32px;
  width: 100%;
  max-width: 340px;
  text-align: center;

  @media (max-width: 600px) {
    padding: 32px 24px;
  }
`;

export const LoginLogo = styled.div`
  margin-bottom: 4px;

  img {
    height: 36px;
    object-fit: contain;
  }
`;

export const LoginSubtitle = styled.p`
  font-size: 13px;
  color: ${c.textSec};
  margin: 4px 0 28px;
`;

export const LoginInput = styled.input`
  width: 100%;
  padding: 11px 14px;
  font-size: 16px;
  border: none;
  border-radius: 10px;
  background: ${c.bg};
  margin-bottom: ${({ $last }) => $last ? '20px' : '8px'};
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
  color: ${c.text};
  transition: box-shadow 0.15s;

  &:focus {
    box-shadow: 0 0 0 3px rgba(0,122,255,0.3);
  }

  &::placeholder {
    color: ${c.textTer};
  }
`;

export const LoginError = styled.p`
  color: ${c.danger};
  font-size: 13px;
  margin: 0 0 12px;
`;

export const LoginButton = styled.button`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  background: ${c.accent};
  color: #FFF;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover {
    background: ${c.accentHover};
  }

  &:active {
    transform: scale(0.98);
  }
`;

// ===== FORM =====

export const FormSection = styled.div`
  background: ${c.card};
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.25s ease;

  @media (max-width: 600px) {
    border-radius: 0;
    padding: 16px;
  }
`;

export const FormTitle = styled.h2`
  font-size: 13px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: ${c.textSec};
  margin: 0 0 12px;
  text-align: left;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;

  ${({ $full }) => $full && `grid-column: 1 / -1;`}

  label {
    font-size: 13px;
    font-weight: 400;
    color: ${c.textSec};
  }

  input, select, textarea {
    font-family: inherit;
    font-size: 16px;
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    background: ${c.bg};
    color: ${c.text};
    outline: none;
    transition: box-shadow 0.15s;

    &:focus {
      box-shadow: 0 0 0 3px rgba(0,122,255,0.25);
    }

    &::placeholder {
      color: ${c.textTer};
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }

  select {
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%238E8E93' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 32px;
  }
`;

// ===== TOGGLE SWITCH =====

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;

  span {
    font-size: 16px;
    font-weight: 400;
    color: ${c.text};
  }
`;

export const ToggleSwitch = styled.button`
  position: relative;
  width: 51px;
  height: 31px;
  border-radius: 16px;
  border: none;
  cursor: pointer;
  transition: background 0.25s ease;
  flex-shrink: 0;
  background: ${({ $on }) => $on ? c.success : '#E5E5EA'};
  padding: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ $on }) => $on ? '22px' : '2px'};
    width: 27px;
    height: 27px;
    border-radius: 50%;
    background: #FFF;
    box-shadow: 0 2px 4px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.1);
    transition: left 0.25s ease;
  }

  &:active::after {
    width: 31px;
    ${({ $on }) => $on ? 'left: 18px;' : ''}
  }
`;

// ===== LAZY IMAGE =====

export const ImgSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    #E5E5EA 0%,
    #F2F2F7 40%,
    #E5E5EA 80%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease infinite;
`;

export const ImgLoaded = styled.img`
  animation: ${imgFadeIn} 0.25s ease;
`;

// ===== IMAGE VIEWER =====

export const ViewerContainer = styled.div`
  position: relative;
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
  user-select: none;
  min-height: 200px;

  img, video {
    width: 100%;
    max-height: 400px;
    object-fit: contain;
    display: block;
  }

  ${ImgSkeleton} {
    min-height: 200px;
    background: linear-gradient(90deg, #1a1a1a 0%, #2a2a2a 40%, #1a1a1a 80%);
    background-size: 200% 100%;
  }

  @media (max-width: 600px) {
    border-radius: 8px;

    img, video {
      max-height: 260px;
    }
  }
`;

export const ViewerCounter = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: rgba(255,255,255,0.9);
  font-size: 13px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
  font-variant-numeric: tabular-nums;
`;

export const ViewerClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: rgba(255,255,255,0.9);
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover {
    background: rgba(0,0,0,0.7);
  }
`;

export const ViewerNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $dir }) => $dir === 'prev' ? 'left: 8px;' : 'right: 8px;'}
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover {
    background: rgba(255,255,255,0.3);
  }

  @media (max-width: 600px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

export const ViewerDelete = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);

  button {
    background: rgba(255,59,48,0.8);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 7px 18px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s;

    &:hover {
      background: rgba(255,59,48,1);
    }
  }
`;

// ===== PAGINATION =====

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
  color: ${c.textSec};

  button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: ${c.accent};
    font-family: inherit;
    padding: 4px 8px;
    border-radius: 6px;
    transition: background 0.12s;

    &:hover:not(:disabled) {
      background: rgba(0,122,255,0.08);
    }

    &:disabled {
      color: ${c.textTer};
      cursor: not-allowed;
    }
  }
`;

// ===== CHIPS =====

export const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ $gap }) => $gap || '8px'};
  margin-bottom: ${({ $mb }) => $mb || '16px'};
`;

export const Chip = styled.span`
  background: ${c.bg};
  padding: ${({ $compact }) => $compact ? '5px 10px' : '6px 12px'};
  border-radius: 8px;
  font-size: ${({ $compact }) => $compact ? '14px' : '15px'};
  font-family: ${({ $mono }) => $mono ? "ui-monospace, 'SF Mono', monospace" : 'inherit'};
  display: flex;
  align-items: center;
  gap: 6px;

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: ${c.textSec};
    font-size: 16px;
    padding: 0;
    line-height: 1;
    transition: color 0.12s;
    display: flex;

    &:hover {
      color: ${c.danger};
    }
  }
`;

export const InlineInputRow = styled.div`
  display: flex;
  gap: 8px;

  input {
    flex: 1;
    font-size: 16px;
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    background: ${c.bg};
    font-family: ${({ $mono }) => $mono ? "ui-monospace, 'SF Mono', monospace" : 'inherit'};
    outline: none;
    color: ${c.text};
    transition: box-shadow 0.15s;

    &:focus {
      box-shadow: 0 0 0 3px rgba(0,122,255,0.25);
    }

    &::placeholder {
      color: ${c.textTer};
    }
  }
`;

export const HelpText = styled.p`
  font-size: 13px;
  color: ${c.textSec};
  margin: 0 0 16px;
  text-align: left;
  line-height: 1.4;
`;

export const EmptyChipText = styled.span`
  font-size: 14px;
  color: ${c.textTer};
`;

// ===== IMAGE UPLOAD =====

export const ImageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

export const ImageThumb = styled.div`
  position: relative;
  width: 72px;
  height: 54px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.12s;

  ${({ $active }) => $active
    ? `box-shadow: 0 0 0 2.5px ${c.accent}; transform: scale(1.04);`
    : ''
  }

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  button {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(0,0,0,0.55);
    color: #FFF;
    border: none;
    font-size: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.12s;
    backdrop-filter: blur(4px);
  }

  &:hover button {
    opacity: 1;
  }

  @media (max-width: 600px) {
    button {
      opacity: 1;
    }
  }
`;

export const UploadZone = styled.label`
  width: 72px;
  height: 54px;
  border-radius: 6px;
  border: 2px dashed ${c.border};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${c.textTer};
  font-size: 22px;
  font-weight: 300;
  transition: all 0.12s;

  &:hover {
    border-color: ${c.accent};
    color: ${c.accent};
    background: rgba(0,122,255,0.04);
  }

  input { display: none; }
`;

// ===== MISC =====

export const EmptyState = styled.div`
  text-align: center;
  padding: 80px 24px;
  color: ${c.textTer};
  font-size: 16px;
`;

export const BackLink = styled.button`
  background: none;
  border: none;
  color: ${c.accent};
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 2px;

  &:hover { opacity: 0.7; }
`;

export const ActionsSection = styled.div`
  padding: 0 16px;
  margin-bottom: 16px;

  @media (max-width: 600px) {
    padding: 0 16px;
  }
`;

